import React from "react";
import { Link } from "react-router-dom";
import { ArrowRight, ExternalLink, Mail, Phone, Award, FileText, Mic, Newspaper } from "lucide-react";
import { useLanguage } from "@/contexts/LanguageContext";
import BrandMonogramEdge from "@/components/BrandMonogramEdge";
import PageHero from "@/components/PageHero";
import PageStickyNav from "@/components/PageStickyNav";

const pick = (obj, lang) => {
  if (!obj) return "";
  if (typeof obj === "string") return obj;
  return obj[lang] || obj.en || obj.fr || "";
};

const PRESS_HERO_IMAGE =
  "https://images.unsplash.com/photo-1518684079-3c830dcef090?auto=format&fit=crop&w=2400&q=80";

// Editorial articles — placeholder records the brand can curate over time.
// Each item supports a per-locale title; description and outlet are language-agnostic.
const ARTICLES = [
  {
    id: "conde-nast-2024",
    outlet: "Condé Nast Traveler",
    type: "feature",
    date: "2024-11",
    title: {
      en: "Inside the Sahara's most surprising luxury camp",
      fr: "Dans le camp de luxe le plus surprenant du Sahara",
      es: "Dentro del campamento de lujo más sorprendente del Sáhara",
    },
    excerpt: {
      en: "An immersive long-read about the family behind Grup Xaluca and the night that changed how the author saw the desert.",
      fr: "Un long format immersif sur la famille à la tête de Grup Xaluca et la nuit qui a changé la façon dont l'auteure voit le désert.",
      es: "Un reportaje inmersivo sobre la familia detrás de Grup Xaluca y la noche que cambió la mirada de la autora sobre el desierto.",
    },
    href: "#",
    image: "https://images.unsplash.com/photo-1517825738774-7de9363ef735?auto=format&fit=crop&w=1600&q=80",
  },
  {
    id: "monocle-2024",
    outlet: "Monocle",
    type: "feature",
    date: "2024-06",
    title: {
      en: "A house, not a hotel — the Xaluca way",
      fr: "Une maison, pas un hôtel — la façon Xaluca",
      es: "Una casa, no un hotel — la manera Xaluca",
    },
    excerpt: {
      en: "Monocle's design editor visits the Erfoud kasbah and writes about hospitality measured in mint tea and silence.",
      fr: "La rédactrice design de Monocle visite la kasbah d'Erfoud et raconte une hospitalité qui se mesure en thé à la menthe et en silence.",
      es: "La editora de diseño de Monocle visita la kasbah de Erfoud y escribe sobre una hospitalidad medida en té con menta y silencio.",
    },
    href: "#",
    image: "https://images.unsplash.com/photo-1542401886-65d6c61db217?auto=format&fit=crop&w=1600&q=80",
  },
  {
    id: "nyt-2023",
    outlet: "The New York Times — Travel",
    type: "feature",
    date: "2023-12",
    title: {
      en: "36 hours in the south of Morocco",
      fr: "36 heures dans le sud du Maroc",
      es: "36 horas en el sur de Marruecos",
    },
    excerpt: {
      en: "The Times' Morocco itinerary spends the second day at Kasbah Hotel Xaluca Arfoud — \"the rare property that improves the more time you give it.\"",
      fr: "L'itinéraire marocain du Times consacre sa seconde journée au Kasbah Hotel Xaluca Arfoud — « la rare adresse qui s'améliore plus on lui accorde de temps ».",
      es: "El itinerario marroquí del Times dedica el segundo día al Kasbah Hotel Xaluca Arfoud — «la rara propiedad que mejora cuanto más tiempo se le concede».",
    },
    href: "#",
    image: "https://images.unsplash.com/photo-1551632811-561732d1e306?auto=format&fit=crop&w=1600&q=80",
  },
  {
    id: "vogue-2023",
    outlet: "Vogue Espagne",
    type: "interview",
    date: "2023-09",
    title: {
      en: "Tayeb & Lluís: the friendship behind one of Morocco's most quietly influential hotel groups",
      fr: "Tayeb et Lluís : l'amitié derrière l'un des groupes hôteliers les plus discrètement influents du Maroc",
      es: "Tayeb y Lluís: la amistad detrás de uno de los grupos hoteleros más discretamente influyentes de Marruecos",
    },
    excerpt: {
      en: "A long-form conversation with the two co-founders about thirty-five years of building hotels at the edge of the desert.",
      fr: "Un long entretien avec les deux co-fondateurs sur trente-cinq ans à bâtir des hôtels en bordure du désert.",
      es: "Una larga conversación con los dos cofundadores sobre treinta y cinco años construyendo hoteles al borde del desierto.",
    },
    href: "#",
    image: "https://images.unsplash.com/photo-1531123897727-8f129e1688ce?auto=format&fit=crop&w=1600&q=80",
  },
  {
    id: "telegraph-2023",
    outlet: "The Telegraph — Travel",
    type: "mention",
    date: "2023-04",
    title: {
      en: "10 desert camps that justify the journey",
      fr: "10 camps du désert qui justifient le voyage",
      es: "10 campamentos del desierto que justifican el viaje",
    },
    excerpt: {
      en: "La Belle Étoile Luxury Desert Camp ranked #2 on The Telegraph's global list — \"the suite tent is the most considered we've slept in anywhere on the continent.\"",
      fr: "La Belle Étoile Luxury Desert Camp classée n°2 du classement mondial du Telegraph — « la tente-suite est la plus pensée dans laquelle nous ayons dormi sur le continent ».",
      es: "La Belle Étoile Luxury Desert Camp clasificada n°2 en la lista mundial de The Telegraph — «la tienda-suite es la más cuidada en la que hemos dormido en el continente».",
    },
    href: "#",
    image: "https://images.unsplash.com/photo-1532978879514-6cb1cebaaa11?auto=format&fit=crop&w=1600&q=80",
  },
  {
    id: "elle-deco-2022",
    outlet: "Elle Decoration",
    type: "feature",
    date: "2022-10",
    title: {
      en: "The vocabulary of pisé — Morocco's earthen architecture, rewritten",
      fr: "Le vocabulaire du pisé — l'architecture de terre marocaine, réécrite",
      es: "El vocabulario del tapial — la arquitectura de tierra marroquí, reescrita",
    },
    excerpt: {
      en: "An in-depth feature on the architectural language of the Xaluca properties — and the masons who keep it standing.",
      fr: "Un dossier approfondi sur le langage architectural des adresses Xaluca — et les maçons qui les maintiennent debout.",
      es: "Un reportaje en profundidad sobre el lenguaje arquitectónico de las direcciones Xaluca — y los albañiles que las mantienen en pie.",
    },
    href: "#",
    image: "https://images.unsplash.com/photo-1541518763669-27fef04b14ea?auto=format&fit=crop&w=1600&q=80",
  },
];

const AWARDS = [
  { year: "2024", body: "Travel + Leisure", text: { en: "World's Best Awards — Top 5 hotels in North Africa", fr: "World's Best Awards — Top 5 des hôtels en Afrique du Nord", es: "World's Best Awards — Top 5 hoteles en África del Norte" } },
  { year: "2024", body: "Condé Nast Traveler", text: { en: "Readers' Choice Awards — Best in the Desert", fr: "Readers' Choice Awards — Best in the Desert", es: "Readers' Choice Awards — Mejor en el Desierto" } },
  { year: "2023", body: "Tripadvisor", text: { en: "Travelers' Choice Best of the Best — Erg Chebbi", fr: "Travelers' Choice Best of the Best — Erg Chebbi", es: "Travelers' Choice Best of the Best — Erg Chebbi" } },
  { year: "2023", body: "UNESCO partnership", text: { en: "Heritage Restoration partnership — Aït Ben Haddou", fr: "Partenariat de restauration du patrimoine — Aït Ben Haddou", es: "Acuerdo de restauración patrimonial — Aït Ben Haddou" } },
];

const TYPE_META = {
  feature: {
    icon: FileText,
    label: { en: "Feature", fr: "Reportage", es: "Reportaje" },
  },
  interview: {
    icon: Mic,
    label: { en: "Interview", fr: "Entretien", es: "Entrevista" },
  },
  mention: {
    icon: Newspaper,
    label: { en: "Mention", fr: "Mention", es: "Mención" },
  },
};

const PressPage = () => {
  const { lang } = useLanguage();

  return (
    <div data-testid="page-press" className="bg-[#FDFBF7] text-[#2C2621]">
      {/* HERO — multi-slide (video first, then press photography) */}
      <section data-testid="press-hero">
        <PageHero
          images={[
            PRESS_HERO_IMAGE,
            "https://customer-assets.emergentagent.com/job_xaluca-luxury/artifacts/htd6p1fu_Kasbah%20Hotel%20Xaluca%20-%20Ae%CC%81reas%20-%20HamidBabni-4.jpg",
            "https://customer-assets.emergentagent.com/job_xaluca-luxury/artifacts/6pvzp897_Kasbah%20Hotel%20Xaluca%20-%20Ae%CC%81reas%20-%20HamidBabni-1.jpg",
          ]}
          alt="Press · Grup Xaluca"
          scope="page.press"
          overline={pick({ en: "Press & media", fr: "Presse & médias", es: "Prensa y medios" }, lang)}
          title={pick({
            en: "Where Grup Xaluca lives, in print and on screen.",
            fr: "Où vit Grup Xaluca, sur papier et à l'écran.",
            es: "Donde vive Grup Xaluca, en papel y en pantalla.",
          }, lang)}
          sub={pick({
            en: "A curated archive of external publications, interviews, articles and media mentions about our houses, our family and the south of Morocco. Updated as new stories are published.",
            fr: "Une archive sélective de publications externes, entretiens, articles et mentions médiatiques sur nos maisons, notre famille et le sud du Maroc. Mis à jour à mesure que de nouveaux récits paraissent.",
            es: "Un archivo curado de publicaciones externas, entrevistas, artículos y menciones mediáticas sobre nuestras casas, nuestra familia y el sur de Marruecos. Actualizado conforme se publican nuevas historias.",
          }, lang)}
        />
      </section>

      <PageStickyNav
        items={[
          { id: "press-intro", label: { en: "Intro", fr: "Intro", es: "Intro" } },
          { id: "press-articles", label: { en: "Articles", fr: "Articles", es: "Artículos" } },
          { id: "press-awards", label: { en: "Awards", fr: "Prix", es: "Premios" } },
          { id: "press-contact", label: { en: "Contact", fr: "Contact", es: "Contacto" } },
        ]}
      />

      {/* INTRO */}
      <section id="press-intro" className="py-20 md:py-28 scroll-mt-32" data-testid="press-intro">
        <div className="max-w-5xl mx-auto px-6 md:px-12">
          <p className="overline">
            {pick({ en: "Editorial archive", fr: "Archives éditoriales", es: "Archivo editorial" }, lang)}
          </p>
          <h2 className="font-serif-x mt-4 text-3xl md:text-4xl lg:text-5xl tracking-tight leading-[1.1] text-[#2C2621]">
            {pick({
              en: "Every article, every interview, every mention — in one place.",
              fr: "Chaque article, chaque entretien, chaque mention — au même endroit.",
              es: "Cada artículo, cada entrevista, cada mención — en un solo lugar.",
            }, lang)}
          </h2>
          <p className="mt-7 text-lg text-[#5C5248] leading-relaxed">
            {pick({
              en: "Travel publications, design magazines and culture journals have followed Grup Xaluca for more than two decades. This page gathers the most significant external coverage in chronological order — a living archive that we update each time a new story is published about us. For new editorial enquiries, see the contact block at the bottom of this page.",
              fr: "Les publications de voyage, magazines de design et revues culturelles suivent Grup Xaluca depuis plus de deux décennies. Cette page rassemble les couvertures externes les plus significatives par ordre chronologique — une archive vivante que nous mettons à jour à chaque nouvelle parution. Pour les nouvelles demandes éditoriales, voir le bloc contact en bas de page.",
              es: "Publicaciones de viajes, revistas de diseño y revistas culturales siguen a Grup Xaluca desde hace más de dos décadas. Esta página reúne las coberturas externas más significativas en orden cronológico — un archivo vivo que actualizamos cada vez que se publica una nueva historia sobre nosotros. Para nuevas consultas editoriales, consulte el bloque de contacto al pie de página.",
            }, lang)}
          </p>
        </div>
      </section>

      {/* ARTICLES GRID */}
      <section id="press-articles" className="pb-20 md:pb-28 scroll-mt-32" data-testid="press-articles">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid md:grid-cols-12 gap-6 mb-12">
            <div className="md:col-span-7">
              <p className="overline">
                {pick({ en: "Publications & interviews", fr: "Publications & entretiens", es: "Publicaciones y entrevistas" }, lang)}
              </p>
              <h2 className="font-serif-x mt-3 text-3xl md:text-4xl tracking-tight leading-[1.1]">
                {pick({
                  en: "Featured external coverage",
                  fr: "Couvertures externes en vedette",
                  es: "Coberturas externas destacadas",
                }, lang)}
              </h2>
            </div>
            <div className="md:col-span-5 md:flex md:items-end md:justify-end">
              <p className="text-sm text-[#5C5248]">
                {pick({
                  en: `${ARTICLES.length} articles · curated archive`,
                  fr: `${ARTICLES.length} articles · archive sélective`,
                  es: `${ARTICLES.length} artículos · archivo curado`,
                }, lang)}
              </p>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8">
            {ARTICLES.map((a) => {
              const meta = TYPE_META[a.type] || TYPE_META.feature;
              const Icon = meta.icon;
              return (
                <a
                  key={a.id}
                  href={a.href}
                  target={a.href && a.href !== "#" ? "_blank" : undefined}
                  rel={a.href && a.href !== "#" ? "noopener noreferrer" : undefined}
                  className="group bg-white border border-[#2C2621]/10 hover:border-[#C16542]/40 transition-colors flex flex-col"
                  data-testid={`press-article-${a.id}`}
                  data-cms-scope={`press-article.${a.id}`}
                >
                  <div className="relative aspect-[16/10] overflow-hidden">
                    <img
                      src={a.image}
                      alt={pick(a.title, lang)}
                      className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" loading="lazy" decoding="async"
                      data-cms-role="card.image"/>
                    <span className="absolute top-4 left-4 inline-flex items-center gap-1.5 bg-[#1A1513]/85 text-[#FDFBF7] text-[10px] tracking-[0.25em] uppercase px-3 py-1.5">
                      <Icon className="w-3 h-3" strokeWidth={1.6} />
                      {pick(meta.label, lang)}
                    </span>
                  </div>
                  <div className="p-6 md:p-7 flex flex-col flex-1">
                    <div className="flex items-center justify-between text-[11px] tracking-[0.2em] uppercase text-[#5C5248]">
                      <span className="text-[#C16542]">{a.outlet}</span>
                      <span>{a.date}</span>
                    </div>
                    <h3 className="font-serif-x text-xl md:text-2xl mt-3 leading-snug text-[#2C2621] group-hover:text-[#C16542] transition-colors">
                      {pick(a.title, lang)}
                    </h3>
                    <p className="mt-3 text-[14px] text-[#5C5248] leading-relaxed flex-1">
                      {pick(a.excerpt, lang)}
                    </p>
                    <span className="mt-5 inline-flex items-center gap-2 text-[11px] tracking-[0.25em] uppercase text-[#2C2621] group-hover:text-[#C16542] border-b border-[#2C2621]/30 group-hover:border-[#C16542] pb-1 w-fit transition-colors">
                      {pick({ en: "Read the article", fr: "Lire l'article", es: "Leer el artículo" }, lang)}
                      <ExternalLink className="w-3 h-3" strokeWidth={1.5} />
                    </span>
                  </div>
                </a>
              );
            })}
          </div>
        </div>
      </section>

      {/* AWARDS & RECOGNITION */}
      <section id="press-awards" className="py-20 md:py-28 bg-[#1A1513] text-[#FDFBF7] scroll-mt-32" data-testid="press-awards">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid md:grid-cols-12 gap-10 md:gap-16">
            <div className="md:col-span-5">
              <Award className="w-8 h-8 text-[#D4A373]" strokeWidth={1.5} />
              <p className="overline mt-5 !text-[#D4A373]">
                {pick({ en: "Awards & recognition", fr: "Prix & reconnaissances", es: "Premios y reconocimientos" }, lang)}
              </p>
              <h2 className="font-serif-x mt-3 text-4xl md:text-5xl tracking-tight leading-[1.05]">
                {pick({
                  en: "Recognised, quietly.",
                  fr: "Reconnus, discrètement.",
                  es: "Reconocidos, discretamente.",
                }, lang)}
              </h2>
              <p className="mt-6 text-[#FDFBF7]/75 leading-relaxed">
                {pick({
                  en: "Recent distinctions from publications and institutions we hold in high regard. A complete list is available on request.",
                  fr: "Distinctions récentes de publications et d'institutions que nous tenons en haute estime. Liste complète sur demande.",
                  es: "Distinciones recientes de publicaciones e instituciones que apreciamos. Lista completa a petición.",
                }, lang)}
              </p>
            </div>
            <div className="md:col-span-7">
              <ul className="divide-y divide-[#FDFBF7]/15">
                {AWARDS.map((aw) => (
                  <li
                    key={`${aw.year}-${aw.body}`}
                    className="py-5 grid grid-cols-12 gap-4 items-baseline"
                    data-testid={`press-award-${aw.year}-${aw.body.toLowerCase().replace(/\s+/g, "-")}`}
                  >
                    <span className="col-span-2 font-serif-x text-2xl text-[#D4A373]">{aw.year}</span>
                    <span className="col-span-4 text-sm tracking-[0.15em] uppercase text-[#FDFBF7]/85">{aw.body}</span>
                    <span className="col-span-6 text-[15px] text-[#FDFBF7]/95 leading-snug">{pick(aw.text, lang)}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* PRESS CONTACT */}
      <section id="press-contact" className="py-20 md:py-28 bg-[#F2EBE1]/40 scroll-mt-32" data-testid="press-contact">
        <div className="max-w-5xl mx-auto px-6 md:px-12 text-center">
          <p className="overline">
            {pick({ en: "Editorial enquiries", fr: "Demandes éditoriales", es: "Consultas editoriales" }, lang)}
          </p>
          <h2 className="font-serif-x mt-4 text-3xl md:text-4xl lg:text-5xl tracking-tight leading-[1.08] text-[#2C2621]">
            {pick({
              en: "Working on a story? Write to us directly.",
              fr: "Vous préparez un sujet ? Écrivez-nous directement.",
              es: "¿Está preparando un reportaje? Escríbanos directamente.",
            }, lang)}
          </h2>
          <p className="mt-6 max-w-2xl mx-auto text-base md:text-lg text-[#5C5248] leading-relaxed">
            {pick({
              en: "Our press team replies to journalists, partners and travel publications within one business day. Please share a short brief of your story, the publication, and any timing constraints.",
              fr: "Notre équipe presse répond aux journalistes, partenaires et publications sous un jour ouvré. Merci d'indiquer brièvement votre sujet, votre publication et vos contraintes de calendrier.",
              es: "Nuestro equipo de prensa responde a periodistas, socios y publicaciones en un día laborable. Indique brevemente su tema, la publicación y sus plazos.",
            }, lang)}
          </p>

          <div className="mt-10 grid sm:grid-cols-2 gap-4 max-w-2xl mx-auto">
            <a
              href="mailto:press@xaluca.com"
              className="inline-flex items-center justify-center gap-3 bg-[#C16542] hover:bg-[#A35133] text-[#FDFBF7] px-7 py-4 text-xs tracking-[0.25em] uppercase transition-colors"
              data-testid="press-contact-email"
            >
              <Mail className="w-4 h-4" strokeWidth={1.5} />
              press@xaluca.com
            </a>
            <a
              href="tel:+212535578450"
              className="inline-flex items-center justify-center gap-3 border border-[#2C2621]/30 text-[#2C2621] hover:border-[#C16542] hover:text-[#C16542] px-7 py-4 text-xs tracking-[0.25em] uppercase transition-colors"
              data-testid="press-contact-phone"
            >
              <Phone className="w-4 h-4" strokeWidth={1.5} />
              +212 535 57 84 50
            </a>
          </div>

          <p className="mt-10 text-xs tracking-[0.25em] uppercase text-[#5C5248]">
            {pick({ en: "Or return to", fr: "Ou retournez à", es: "O vuelva a" }, lang)}
            {" "}
            <Link to="/about" className="text-[#C16542] hover:text-[#A35133] inline-flex items-center gap-1" data-testid="press-back-about">
              {pick({ en: "the about page", fr: "la page À propos", es: "la página Sobre nosotros" }, lang)}
              <ArrowRight className="w-3 h-3" strokeWidth={1.5} />
            </Link>
          </p>
        </div>
      </section>
    </div>
  );
};

export default PressPage;
